import React, { useRef, useState, useEffect } from "react";
import { Box, IconButton, Slider, SliderTrack, SliderFilledTrack, SliderThumb } from "@chakra-ui/react";
import { FaPlay, FaPause } from "react-icons/fa";

interface AudioPlayerProps {
    audioSrc: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioSrc }) => {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [audioLoaded, setAudioLoaded] = useState(false);

    useEffect(() => {
        // Reset state when audioSrc changes
        setIsPlaying(false);
        setProgress(0);
        setDuration(0);
        setAudioLoaded(false);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
    }, [audioSrc]);

    const togglePlay = () => {
        if (!audioLoaded) {
            // Create and load audio only on first play
            const audio = new Audio(audioSrc);
            audioRef.current = audio;

            audio.addEventListener("loadedmetadata", () => {
                if (audio.duration && Number.isFinite(audio.duration)) {
                    setDuration(audio.duration);
                }
                setAudioLoaded(true);
                audio.play();
                setIsPlaying(true);
            });
            audio.addEventListener("timeupdate", () => {
                if (audio.duration && Number.isFinite(audio.duration)) {
                    setProgress((audio.currentTime / audio.duration) * 100);
                }
            });
            audio.addEventListener("ended", () => {
                setIsPlaying(false);
            });
            audio.load();
        } else {
            const audio = audioRef.current;
            if (!audio) return;
            if (isPlaying) {
                audio.pause();
                setIsPlaying(false);
            } else {
                audio.play();
                setIsPlaying(true);
            }
        }
    };

    const handleSeek = (val: number) => {
        const audio = audioRef.current;
        if (!audioLoaded || !audio || !duration) return;

        const newProgress = val;
        const newTime = (duration * newProgress) / 100;
        audio.currentTime = newTime;
        setProgress(newProgress);
    };

    return (
        <Box display="flex" alignItems="center" gap={4} width="100%">
            <IconButton
                icon={isPlaying ? <FaPause /> : <FaPlay />}
                onClick={togglePlay}
                aria-label="Play/Pause"
            />
            <Slider
                aria-label="Audio progress"
                min={0}
                max={100}
                value={progress}
                onChange={handleSeek}
                width={{ base: "100%", md: "300px" }}
                isDisabled={!audioLoaded}
            >
                <SliderTrack>
                    <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
            </Slider>
        </Box>
    );
};

export default AudioPlayer;
